import { useRef, useState, useEffect } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMapEvents } from 'react-leaflet';
import { divIcon } from 'leaflet';
import { t } from 'ttag';
import Btn from 'components/Btn/index.js';
import { LEAFLET_TILELAYER_URL } from 'constants.js';
import IconExpand from 'static/icons/expand.svg';
import IconCompress from 'static/icons/compress.svg';
import IconCircleQuestion from 'static/icons/circle-question.svg';
import 'leaflet/dist/leaflet.css';
import './index.scss';

// eslint-disable-next-line no-console
console.log('We use Leaflet (https://leafletjs.com) to render the maps in this app.');

const DEFAULT_MAP_PROPS = Object.freeze({
    attributionControl: false,
    doubleClickZoom: true,
    dragging: true,
    maxZoom: 18,
    minZoom: 5,
    scrollWheelZoom: true,
    touchZoom: true,
    trackResize: false,
    zoom: 12,
    zoomControl: false,
    zoomDelta: true,
    zoomSnap: 1,
});

const DefaultIcon = divIcon({
    className: 'map-default-marker-icon',
    iconSize: [48, 48],
    popupAnchor: [0, -12],
});

const MapEvents = ({ onClick }) => useMapEvents({ click: onClick }) && null;

const RenderMarker = (marker, i) => {
    if(Array.isArray(marker)) {
        const [lat, lng] = marker;

        return (lat && lng) && <Marker key={i} position={marker} icon={DefaultIcon} />;
    } else {
        const { pos, content, onClick, onMouseOver, onMouseOut, ...rest } = marker;
        const [lat, lng] = pos;

        const eventHandlers = [onClick, onMouseOver, onMouseOver].some(v => v)
            ? {
                ...(onClick && { click: onClick }),
                ...(onMouseOver && { mouseover: onMouseOver }),
                ...(onMouseOut && { mouseout: onMouseOut }),
            } : undefined;

        return (lat && lng) && (
            <Marker key={i} {...rest} position={pos} icon={DefaultIcon} eventHandlers={eventHandlers}>
                <Popup>{content}</Popup>
            </Marker>
        );
    }
};

const MapComponent = ({
    center,
    markers,
    onCreated,
    onClick,
    expandable = false,
    onClickToggleExpanded,
    invalidator,
    className = '',
    ...rest
}) => {
    const refMap = useRef(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [lat, lng] = center;

    useEffect(() => { refMap.current?.invalidateSize(); }, [isExpanded]);

    useEffect(() => { setTimeout(() => refMap.current?.invalidateSize()); }, [invalidator]);

    const _onClickToggleExpanded = () => {
        setIsExpanded(!isExpanded);
        onClickToggleExpanded?.(!isExpanded);
    };

    const whenCreated = map => {
        refMap.current = map;
        onCreated?.(map);
    };

    return (
        <div className={`map-component ${className} ${isExpanded ? 'expanded' : ''}`}>
            {(!lat && !lng)
                ? <IconCircleQuestion className='map-placeholder' />
                : (
                    <>
                        {expandable && (
                            <Btn
                                onClick={_onClickToggleExpanded}
                                icon={isExpanded ? <IconCompress /> : <IconExpand />}
                                title={isExpanded
                                    ? t`(MAP_COMPONENT)_BTN_CONTRACT_TOOLTIP`
                                    : t`(MAP_COMPONENT)_BTN_EXPAND_TOOLTIP`}
                                className='btn-expand'
                            />
                        )}
                        <MapContainer {...DEFAULT_MAP_PROPS} center={center} {...rest} whenCreated={whenCreated}>
                            <TileLayer url={LEAFLET_TILELAYER_URL} noWrap />
                            {onClick && <MapEvents onClick={onClick} />}
                            {markers?.map(RenderMarker)}
                        </MapContainer>
                    </>
                )}
        </div>
    );
};

export default MapComponent;
